'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contextualNodeAttributes"];
import tinymce from 'tinymce';
import { SpacingIconTypes } from 'rich-text-lib/constants/spacing';
import { CANVAS_IMAGE } from '../constants/toolbar';
import { OPEN_IMAGE_PICKER } from '../constants/usageTracking';
import { trackInteraction } from '../utils/usageTracking';
import InsertButton from '../components/toolbar/InsertButton';
import CanvasToolbarButton from 'rich-text-lib/components/toolbar/ToolbarButton';
import { i18nWithNameSpace } from '../utils/i18n';
import { isEditableImage, getImagePluginSetting } from '../utils/image';
import ImagePopoverManager from '../components/ImagePopoverManager';
import { getRemoveImageHandler, getAltTextChangeHandler, getLoadingValueChangeHandler, getImageAttributes, getFetchImageFileInfo } from './handlers';
import { getPluginSetting } from '../utils';
import { hsimageConfig } from '../constants/image';
import { hslinkConfig, LINK_SETTINGS } from '../constants/hslink';
import { getImageContextualToolbar } from '../constants/contextualToolbar';
import { HS_PLUGINS } from '../constants/hsSettings';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = i18nWithNameSpace('TinymcePlugins.hsimage');
export function addControls(editor, pluginName, openImagePicker, openAIImagePicker, openFilePicker) {
  const showMarginControls = !getImagePluginSetting(editor, hsimageConfig.DISABLE_SPACING);
  const {
    [hslinkConfig.ALLOW_FILE_DOWNLOAD]: allowFileDownload = true,
    [hslinkConfig.ALLOW_MEETING_LINKS]: allowMeetingLink,
    [hslinkConfig.ALLOW_ANCHOR_LINKS]: allowAnchorLinks,
    [hslinkConfig.EMAIL_SUBSCRIPTION_LINK_TYPES]: emailSubscriptionLinkTypes = [],
    [hslinkConfig.HIDE_TARGET_BLANK_AND_NO_FOLLOW]: hideRefAndTargetControls
  } = getPluginSetting(editor, LINK_SETTINGS, {});
  const handleInsertImageClick = ({
    isActive
  }) => {
    if (isActive) {
      editor.fire('OpenImageEditPopover');
    } else {
      openImagePicker();
      trackInteraction(editor, 'tinymceImage', OPEN_IMAGE_PICKER, {
        method: 'toolbar'
      });
    }
  };
  const isActiveNodeHandler = ({
    element
  }) => {
    if (!element) return false;
    if (isEditableImage(editor, element)) {
      return element;
    }
    return false;
  };
  const getActiveNodeAttributes = node => {
    return Object.assign({}, getImageAttributes(editor, node), {
      marginIconType: SpacingIconTypes.IMAGE,
      useFullWidth: false
    });
  };
  const contextualToolbarConfig = {
    showMarginControls
  };
  if (editor.getParam(HS_PLUGINS).appName === 'knowledge-base') {
    contextualToolbarConfig.useListButtons = true;
    contextualToolbarConfig.showLineHeight = false;
  }
  tinymce.canvas.addNodeChangeHandler(editor, {
    eventKey: pluginName,
    isActiveNodeHandler,
    getActiveNodeAttributes,
    contextualToolbar: getImageContextualToolbar(contextualToolbarConfig)
  });
  const contextualProps = {
    eventKey: pluginName,
    fetchImageFileInfo: getFetchImageFileInfo(editor),
    allowFileDownload,
    useEmailSubscription: emailSubscriptionLinkTypes.length > 0,
    // @TODO: remove this flag
    emailSubscriptionLinkTypes
  };
  editor.on('mousedown', ({
    target: element
  }) => {
    if (isActiveNodeHandler({
      element
    })) {
      // We need to manually select img elements because the native
      // mousedown + selectionChange events never propagate for img elements
      editor.selection.select(element);
    }
  });
  tinymce.canvas.addButton(editor, CANVAS_IMAGE, {
    renderControl: props => {
      const rest = _objectWithoutPropertiesLoose(props, _excluded);
      return /*#__PURE__*/_jsx(InsertButton, Object.assign({
        "data-test-id": "insert-image-button",
        ButtonComponent: CanvasToolbarButton,
        editor: editor,
        iconName: "insertImage",
        i18nKey: i18nKey,
        onClick: handleInsertImageClick,
        isActiveHandler: isActiveNodeHandler
      }, rest));
    }
  });
  tinymce.canvas.addMenuItem(editor, CANVAS_IMAGE, {
    text: i18nKey('menuItem'),
    onClick: handleInsertImageClick
  });
  tinymce.canvas.addPopover(editor, pluginName, {
    renderPopover(renderProps) {
      return /*#__PURE__*/_jsx(ImagePopoverManager, Object.assign({
        editor: editor,
        onSrcChange: openImagePicker,
        onSelectFile: openFilePicker,
        onImageRemove: getRemoveImageHandler(editor),
        onAltTextChange: getAltTextChangeHandler(editor),
        onLoadingValueChange: getLoadingValueChangeHandler(editor),
        allowMeetingLink: allowMeetingLink,
        allowAnchorLinks: allowAnchorLinks,
        showRefAndTargetControls: !hideRefAndTargetControls
      }, contextualProps, renderProps));
    }
  });
}